import { storeToRefs } from 'pinia';
import { AxiosResponse } from 'axios';
import { ICallResult } from '../../../stores/core/models';
import { useCoreStore } from '../../../stores/core/store';
import useAppI18n from '../../composable/use-app-i18n';
import { PartialDeleteService } from '../../../services/partial-delete.service';
import { normalizeErrorKey } from '../../utils/error-utils';
import { getAccountSignInUrl } from '../../utils/autheris-browser';

/** Must match GPTConcierge.Services.Utils.ErrorCodes.AUTHHERIS_REAUTH_REQUIRED */
const AUTHHERIS_REAUTH_REQUIRED = 'AUTHHERIS_REAUTH_REQUIRED';
const LEGACY_MISSPELLED_REAUTH_REQUIRED = 'AUTHERIS_REAUTH_REQUIRED';

/** Must match ApiBaseController catch (UnauthorizedAccessException) */
const AZURE_AUTH_REQUIRED = 'AZURE_AUTH_REQUIRED';

function isCallResult(callResult: ICallResult | any): callResult is ICallResult {
  return (callResult as ICallResult).data !== undefined;
}

function hasAutherisReauthError(callResult: ICallResult): boolean {
  const targets = new Set([
    AUTHHERIS_REAUTH_REQUIRED.toLowerCase(),
    LEGACY_MISSPELLED_REAUTH_REQUIRED.toLowerCase(),
  ]);

  const hasErrorEntry = callResult.errors?.some((e: any) => {
    const key = String(e?.errorKey ?? e?.errorkey ?? e?.ErrorKey ?? e?.code ?? e?.Code ?? '').toLowerCase();
    return targets.has(key) || key.includes('reauth_required');
  });

  if (hasErrorEntry) return true;

  const asString = String((callResult as any)?.errorsAsString ?? (callResult as any)?.ErrorsAsString ?? '').toLowerCase();
  if (Array.from(targets).some((t) => asString.includes(t))) return true;

  // Last-resort detection for inconsistent payload casing/shape from backend serializers.
  const rawErrorsBlob = JSON.stringify(callResult.errors ?? []).toLowerCase();
  if (Array.from(targets).some((t) => rawErrorsBlob.includes(t))) return true;
  if (rawErrorsBlob.includes('reauth_required')) return true;

  return rawErrorsBlob.includes('not signed in') || rawErrorsBlob.includes('sign in again');
}

function getReauthRedirectUrl(response: AxiosResponse<any>): string {
  const base = String(response?.config?.baseURL ?? '').trim();
  if (base.startsWith('http://') || base.startsWith('https://')) {
    try {
      return `${new URL(base).origin}/Account/SignIn`;
    } catch {
      // fall back below
    }
  }
  return getAccountSignInUrl();
}

function forceBrowserRedirect(url: string): Promise<never> {
  try {
    if (window.location.href !== url) {
      window.location.href = url;
      setTimeout(() => window.location.replace(url), 50);
    }
  } catch {
    window.location.assign(url);
  }
  // Reject immediately to unblock awaiting guards/interceptors.
  return Promise.reject(new Error('AUTH_SIGNIN_REDIRECT'));
}

const responeCallResultInterceptor = (response: AxiosResponse<any>) => {
  const { toastMessage } = storeToRefs(useCoreStore());
  const { t } = useAppI18n();

  if (response.data && isCallResult(response.data)) {
    const callResult: ICallResult = response.data;

    if (hasAutherisReauthError(callResult)) {
      const redirectUrl = getReauthRedirectUrl(response);
      return forceBrowserRedirect(redirectUrl);
    }

    if (callResult.errors?.some((e: any) => {
      const key = String(e?.errorKey ?? e?.errorkey ?? e?.ErrorKey ?? e?.code ?? e?.Code ?? '').toUpperCase();
      return key === AZURE_AUTH_REQUIRED;
    })) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
      return forceBrowserRedirect(`/Account/AzureSignIn?returnUrl=${returnUrl}`);
    }

    // Handle partial delete scenarios for any entity type
    if (callResult.errors?.length) {
      // Try to detect partial delete for common entity types
      const entityTypes = ['indexes', 'chatbots', 'users', 'kiEngines'];

      for (const entityType of entityTypes) {
        const partialDeleteResult = PartialDeleteService.detectPartialDelete(callResult, entityType);

        if (partialDeleteResult.isPartialDelete) {
          // Handle partial delete - show info toast and return deleted count
          const notificationKeys = PartialDeleteService.getPartialDeleteNotificationKeys(entityType);

          toastMessage.value = {
            title: t(notificationKeys.title),
            message: t(notificationKeys.message),
            severity: 'info',
          };

          // Return deleted count for partial delete scenarios
          return partialDeleteResult.deletedCount;
        }
      }
    }

    // Handle all other errors generically
    callResult.errors?.forEach((error: any) => {
      const raw = error?.errorKey ?? error?.code ?? '';
      const normalizedKey = normalizeErrorKey(raw);

      toastMessage.value = {
        message: error?.message || t(normalizedKey) || 'Generic Error',
        severity: 'error',
        title: 'Error',
      };
      console.log('call result interceptor error: ', error);
    });

    if (callResult.errors?.length) {
      throw 'CallResultHasErrors';
    }

    // Return clean payload for successful operations
    return (callResult as any).data;
  }

  // Non-CallResult responses
  return response.data;
};

export default responeCallResultInterceptor;
